import React, { useEffect, useRef, useState, useCallback } from 'react';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './DotBreath.css';

const GLYPH = '숨';
const TARGET_PARTICLES = 1100;
const VOLUME_THRESHOLD = 0.05;
const TENSION = 0.08;
const FRICTION = 0.85;
const GRAVITY_SCALE = 0.32;
const SWAY_SCALE = 1.2;
const MAX_VELOCITY = 14;

type PermState = 'idle' | 'requesting' | 'granted' | 'denied';

interface Particle {
  hx: number; hy: number;   // home position
  x: number; y: number;     // current
  vx: number; vy: number;   // velocity
  seed: number;             // per-particle phase for noise
}

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

// Sample the glyph shape into a list of viewport-centered positions
function sampleGlyph(w: number, h: number): Array<[number, number]> {
  const fontSize = clamp(Math.min(w, h) * 0.55, 200, 500);
  const size = Math.ceil(fontSize * 1.4);
  const off = document.createElement('canvas');
  off.width = size;
  off.height = size;
  const octx = off.getContext('2d');
  if (!octx) return [];

  octx.fillStyle = '#fff';
  octx.textAlign = 'center';
  octx.textBaseline = 'middle';
  octx.font = `900 ${fontSize}px system-ui, -apple-system, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif`;
  octx.fillText(GLYPH, size / 2, size / 2);

  const img = octx.getImageData(0, 0, size, size);
  const positions: Array<[number, number]> = [];
  const stride = Math.max(2, Math.round(fontSize * 0.018));
  for (let y = 0; y < size; y += stride) {
    for (let x = 0; x < size; x += stride) {
      const i = (y * size + x) * 4;
      if (img.data[i + 3] > 100) {
        // small jitter so the sample doesn't look like a perfect grid
        const jx = (Math.random() - 0.5) * stride * 0.6;
        const jy = (Math.random() - 0.5) * stride * 0.6;
        positions.push([x - size / 2 + jx, y - size / 2 + jy]);
      }
    }
  }
  // Shuffle and clip to target count
  for (let i = positions.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [positions[i], positions[j]] = [positions[j], positions[i]];
  }
  return positions.slice(0, TARGET_PARTICLES);
}

const DotBreath: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number>(0);

  // Audio
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioBufRef = useRef<Uint8Array | null>(null);

  // Smoothed RMS
  const smoothRmsRef = useRef(0);
  const sizeRef = useRef({ w: 0, h: 0 });

  const [perm, setPerm] = useState<PermState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [hudRms, setHudRms] = useState(0);
  const [hudCount, setHudCount] = useState(0);
  const hudFrameRef = useRef(0);

  const exp = experiments.find(e => e.id === 'dot-breath');

  // ── Build / rebuild particles for current viewport ─────
  const buildParticles = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    sizeRef.current = { w, h };
    canvas.width = w;
    canvas.height = h;

    const samples = sampleGlyph(w, h);
    const cx = w / 2;
    const cy = h / 2;
    particlesRef.current = samples.map(([dx, dy], i) => ({
      hx: cx + dx,
      hy: cy + dy,
      x: cx + dx,
      y: cy + dy,
      vx: 0,
      vy: 0,
      seed: i * 0.317,
    }));
    setHudCount(particlesRef.current.length);
  }, []);

  // ── Audio setup (sync — must run in a user-gesture for iOS) ─
  const ensureAudio = useCallback(() => {
    let ctx = audioCtxRef.current;
    if (!ctx) {
      ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioCtxRef.current = ctx;
    }
    // iOS unlock
    try {
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
    } catch {/* ignore */}
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    return ctx;
  }, []);

  const start = useCallback(async () => {
    if (perm === 'requesting' || perm === 'granted') return;
    const ctx = ensureAudio();
    setPerm('requesting');
    setErrorMsg(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.2;
      source.connect(analyser);
      streamRef.current = stream;
      analyserRef.current = analyser;
      audioBufRef.current = new Uint8Array(analyser.fftSize);
      setPerm('granted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[dot-breath] mic denied:', msg);
      setErrorMsg('마이크 권한이 필요합니다');
      setPerm('denied');
    }
  }, [perm, ensureAudio]);

  // ── Build particles & wire resize ─────────────────────
  useEffect(() => {
    buildParticles();
    const onResize = () => buildParticles();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [buildParticles]);

  // ── Render loop ───────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const readRms = (): number => {
      const a = analyserRef.current;
      const buf = audioBufRef.current;
      if (!a || !buf) return 0;
      a.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      return Math.sqrt(sum / buf.length);
    };

    let startTime = performance.now();

    const tick = () => {
      const { w, h } = sizeRef.current;
      const t = (performance.now() - startTime) * 0.001; // seconds

      const raw = perm === 'granted' ? readRms() : 0;
      smoothRmsRef.current = 0.7 * smoothRmsRef.current + 0.3 * raw;
      const rms = smoothRmsRef.current;
      const intensity = Math.max(0, rms - VOLUME_THRESHOLD);

      // Clear with slight trail for soft motion blur
      ctx.fillStyle = 'rgba(0, 0, 0, 0.28)';
      ctx.fillRect(0, 0, w, h);

      ctx.fillStyle = '#fff';
      const particles = particlesRef.current;
      const isLoud = intensity > 0;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        if (isLoud) {
          // Gravity + lateral sway (two-band sine acts as cheap "noise")
          const gravity = intensity * GRAVITY_SCALE;
          const sway =
            (Math.sin(t * 1.7 + p.seed * 2.3) * 0.6 +
             Math.sin(t * 4.1 + p.seed * 5.7) * 0.4) * intensity * SWAY_SCALE;
          p.vy += gravity;
          p.vx += sway;
        } else {
          // Spring back toward home — Hooke + dashpot
          const dx = p.hx - p.x;
          const dy = p.hy - p.y;
          p.vx += dx * TENSION;
          p.vy += dy * TENSION;
        }

        p.vx *= FRICTION;
        p.vy *= FRICTION;

        // Velocity clamp keeps the spring stable on huge displacements
        p.vx = clamp(p.vx, -MAX_VELOCITY, MAX_VELOCITY);
        p.vy = clamp(p.vy, -MAX_VELOCITY, MAX_VELOCITY);

        p.x += p.vx;
        p.y += p.vy;

        // Draw — 2×2 white square (fast, looks like a fine dot)
        ctx.fillRect(p.x, p.y, 2, 2);
      }

      // HUD throttle (~10fps)
      hudFrameRef.current++;
      if (hudFrameRef.current % 6 === 0) {
        setHudRms(rms);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [perm]);

  // ── Cleanup audio on unmount ──────────────────────────
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach(tr => tr.stop());
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
      analyserRef.current = null;
      streamRef.current = null;
    };
  }, []);

  return (
    <div className="dotBreath">
      <ExperimentNav prompt={exp?.prompt} title={exp?.title} />
      <canvas ref={canvasRef} className="dotCanvas" />

      {perm !== 'granted' && (
        <div className="dotOverlay">
          <div className="dotOverlayBox">
            <div className="dotOverlayIcon">{GLYPH}</div>
            <div className="dotOverlayTitle">breath becomes motion</div>
            <div className="dotOverlayDesc">
              speak · whisper · sigh<br />
              the dots will fall<br />
              when you stop, they return
            </div>
            {perm === 'denied' && errorMsg && (
              <div className="dotOverlayError">{errorMsg}</div>
            )}
            <button className="dotBtn" onClick={start}>
              {perm === 'idle'       ? 'start' :
               perm === 'requesting' ? '…' :
               perm === 'denied'     ? 'retry' :
                                       ''}
            </button>
          </div>
        </div>
      )}

      {perm === 'granted' && (
        <div className="dotHud">
          <span>rms <span className="hudValue">{hudRms.toFixed(3)}</span></span>
          <span>
            level
            <span className="dotLevelBar">
              <span
                className="dotLevelFill"
                style={{ transform: `scaleX(${Math.min(1, hudRms * 6).toFixed(2)})` }}
              />
            </span>
          </span>
          <span>dots <span className="hudValue">{hudCount}</span></span>
        </div>
      )}
    </div>
  );
};

export default DotBreath;
